import { useState } from "react";
import CallToActionSmall from "../components/v2/call-to-action-sm";
import "../styles/scss/content.module.scss";
import "../styles/scss/security-tabs.module.scss";

const Security = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("Security");

    const tabs = [
        "Security",
        "Data Security", 
        "Compliance",
        "Platform Security",

    ];

    const content = [
        {
            name: "Security",
            heading: "Security",
            points: [
                {
                    title: "Security",
                    description: `
                                <p>At Middleware, security is not just a function — it is a core principle that guides everything we do. From the way our infrastructure is designed, to the safeguards built into our systems, to the training of our employees and the controls within our physical facilities, security is integrated into every layer of our operations. We recognize that protecting customer data is a shared responsibility, and we take that role seriously by adopting a multi-layered approach that blends technology, people, and processes.</p>

                                <p>This means we don’t just rely on one control or one team — we integrate platform safeguards to protect against cyber threats, design for high availability so our services remain reliable, invest in trusted and well-trained personnel who act as the first line of defense, maintain a disciplined patch management to address vulnerabilities quickly, and enforce strong physical protections such as surveillance and biometric access.</p>

                                <p>By combining these measures into a unified security framework, Middleware ensures that your data, applications, and business operations remain safe, resilient, and available — giving you confidence and peace of mind as you scale with us.</p>`
                },
                {
                    title: "Platform & Network Security",
                    description: `
                    <p>Our platform is secured through a defense-in-depth strategy that combines technology, controls, and monitoring:</p>
                    <ul>
                        <li><strong>Network Protection: </strong> Firewalls, intrusion detection/prevention systems (IDS/IPS), and traffic filtering block unauthorized access and suspicious activity.</li>
                        <li><strong>Segmentation:</strong> Internal networks are segmented to limit lateral movement in the event of a breach.</li>
                        <li><strong>Encryption:</strong> All customer data is encrypted at rest and in transit using industry-standard protocols (TLS 1.2+, AES-256).</li>
                        <li><strong>Monitoring & Logging:</strong> Continuous monitoring, real-time alerts, and centralized logging provide visibility into potential threats.</li>
                        <li><strong>Third-Party Assessments:</strong> Regular penetration testing and vulnerability assessments ensure resilience against evolving attack techniques.</li>
                    </ul>`,
                },
                {
                    title: "Availability",
                    description: `
                    <p>We know uptime and reliability are just as important as security. Middleware is designed to ensure services remain accessible and resilient, even in the face of failures or attacks:</p>
                    <ul>
                        <li><strong>Redundancy: </strong> Critical systems are built with redundancy to eliminate single points of failure.</li>
                        <li><strong>Disaster Recovery:</strong> Backups are automated, encrypted, and tested regularly for effectiveness.</li>
                        <li><strong>24/7 Monitoring:</strong> Dedicated teams and automated systems monitor the platform around the clock, with alerts escalating immediately for investigation.</li>
                        <li><strong>Resilience Planning:</strong> Our infrastructure leverages industry-leading cloud providers with global availability zones, ensuring continuity even during regional disruptions.</li>
                        <li><strong>SLA Commitment:</strong> We aim for enterprise-grade uptime targets to meet and exceed customer expectations.</li>
                    </ul>`,
                },
                {
                    title: "Personnel Security",
                    description: `
                    <p>Technology is only as secure as the people who manage it. At Middleware, every employee is part of our security program:</p>
                    <ul>
                        <li><strong>Screening & Onboarding: </strong> Critical systems are built with redundancy to eliminate single points of failure.</li>
                        <li><strong>Access Control:</strong> Employees operate under the principle of least privilege, with strict role-based access enforced across systems.</li>
                        <li><strong>Training & Awareness:</strong> Mandatory security and compliance training is provided at onboarding and refreshed regularly throughout employment.</li>
                        <li><strong>Policies & Enforcement:</strong> Employees are required to follow company security policies, with disciplinary action for non-compliance.</li>
                        <li><strong>Endpoint Security:</strong> All employee devices are secured with disk encryption, anti-malware protection, and enforced patching policies.</li>
                    </ul>`,
                },
                {
                    title: "Patch Management",
                    description: `
                    <p>Unpatched systems are one of the most common ways attackers gain access. Middleware maintains a robust patch management program to stay ahead of threats:</p>
                    <ul>
                        <li><strong>Continuous Monitoring: </strong> We track vendor advisories, threat intelligence feeds, and vulnerability databases to identify newly disclosed risks.</li>
                        <li><strong>Prioritized Updates:</strong> Critical security patches are applied rapidly, while lower-severity updates are scheduled during maintenance windows.</li>
                        <li><strong>Change Control:</strong> Updates are tested in staging environments before deployment to production, minimizing risk of downtime.</li>
                        <li><strong>Customer Communication:</strong> We notify end users in advance of scheduled maintenance and service windows, ensuring transparency.</li>
                        <li><strong>Zero-Day Response:</strong> In the event of an urgent vulnerability, we have escalation processes to deploy patches immediately.</li>
                    </ul>`,
                },
                {
                    title: "Physical Security",
                    description: `
                    <p>Our security commitments extend beyond the digital world — they include the physical spaces where systems and people operate. Middleware’s offices, data centers, and operational sites follow strict controls:</p>
                    <ul>
                        <li><strong>Access Restrictions: </strong> Facilities are secured with biometric access controls (such as fingerprint)</li>
                        <li><strong>Surveillance:</strong> 24/7 CCTV monitoring covers all sensitive areas, including server rooms, office entry points, and restricted zones.</li>
                        <li><strong>Visitor Management:</strong> Guests are registered, always escorted, and granted access only to approved areas.</li>
                        <li><strong>Environmental Controls:</strong> Data center facilities have fire detection and suppression systems, climate control, and backup power systems.</li>
                        <li><strong>Regular Audits:</strong> Physical security practices are reviewed and tested to ensure effectiveness.</li>
                    </ul>
                    <p>Physical protections ensure that only authorized personnel can access Middleware’s sensitive environments, safeguarding against tampering, theft, or physical compromise of systems.</p>`,
                }
            ]
        },
        {
            name: "Data Security",
            heading: "Data Security",
            points: [
                {
                    title: "Data Security",
                    description: `
                                <p>At Middleware, we recognize that data is at the core of every organization’s success, and safeguarding it is a responsibility we take with the highest level of seriousness. Our approach to data security is holistic, ensuring that information is protected at every stage of its lifecycle — from access and storage to transfer and ongoing monitoring. We design our controls not just to meet industry standards, but to anticipate emerging risks and maintain customer trust.</p>`
                },
                {
                    title: "Access Management",
                    description: `
                    <p>Access to sensitive data and systems is tightly controlled through a combination of strong authentication measures, role-based permissions, and continuous review processes. Employees are granted access strictly on a “least privilege” basis, ensuring they only have the minimum level of access necessary to perform their work. Multi-Factor Authentication (MFA) is enforced across all critical systems, and regular access audits are conducted to prevent unauthorized entry. This layered approach helps reduce insider risk and minimizes the chances of misuse or accidental exposure.</p>`,
                },
                {
                    title: "Protection of Customer Data",
                    description: `
                    <p>Customer data is encrypted both in transit and at rest using industry-leading protocols and technologies. This ensures that even in the unlikely event of interception, the data remains unreadable to unauthorized parties. Middleware also employs advanced data loss prevention (DLP) solutions that actively monitor and control the movement of sensitive information. Beyond technical safeguards, we adopt strict internal policies that govern how data is collected, used, and stored — aligning with global privacy regulations such as GDPR, and other applicable laws.</p>`,
                },
                {
                    title: "Data Transfers",
                    description: `
                    <p>We understand the importance of secure data transfers, particularly when information moves across networks or geographies. Middleware leverages secure transmission protocols (such as TLS 1.2+ and HTTPS) to ensure that data exchanges are protected from interception and tampering. For cross-border data transfers, we comply with applicable international standards and legal frameworks, including standard contractual clauses and data processing agreements. This ensures that customer data is safeguarded no matter where it flows.</p>`,
                },
                {
                    title: "Monitoring",
                    description: `
                    <p>Proactive monitoring is central to our data security strategy. We use continuous logging and monitoring tools to track access attempts, unusual activity, and potential threats in real time. automated alerts, allow our team to quickly detect and respond to anomalies. In addition, regular audits and penetration tests are performed to validate the effectiveness of our controls and to identify areas for improvement. Our monitoring processes provide both visibility and accountability, ensuring that any irregularities are addressed before they can become risks. </p>`,
                }
            ]
        },
        {
            name: "Compliance",
            heading: "Compliance",
            points: [
                {
                    title: "Compliance",
                    description: `
                    <p>At Middleware, compliance is at the heart of how we safeguard customer trust and demonstrate accountability. Security is not only about technology — it’s also about adhering to internationally recognized standards, frameworks, and legal obligations that validate our practices and ensure we operate responsibly across industries and jurisdictions.</p>

                    <p>Certifications, Attestations, and Frameworks
                    Middleware aligns its security and privacy programs with leading global standards, ensuring that our customers benefit from independently validated protections. We currently maintain compliance with:</p>
                    <ul>
                        <li><strong>SOC 2: </strong> Demonstrating the strength of our internal controls across security, availability, processing integrity, confidentiality, and privacy.</li>
                        <li><strong>HIPAA:</strong> Ensuring the confidentiality and integrity of sensitive health information, enabling our customers in the healthcare sector to confidently use our platform.</li>
                        <li><strong>GDPR:</strong> Meeting the European Union’s stringent requirements for data privacy, protection, and user rights, while extending these protections to all customers worldwide.</li>
                        <li><strong>ISO 27001:2022:</strong> Adhering to the most recent global standard for information security management, providing a framework for ongoing risk assessment, governance, and continuous improvement.</li>
                    </ul>
                    <p>These certifications and frameworks aren’t just checkmarks — they are living commitments that guide our operations, policies, and continuous improvements to keep customer data safe.</p>`
                },
                {
                    title: "Laws and Regulations",
                    description: `
                    <p>The global regulatory environment is constantly evolving, and Middleware is committed to staying ahead of the curve. Our compliance program ensures adherence to laws such as GDPR, HIPAA, and other regional or sector-specific regulations. By embedding these obligations into our platform and business processes, we help our customers reduce compliance burdens and maintain confidence that their data is being processed legally and ethically.</p>`,
                },
                {
                    title: "Vendor Management",
                    description: `
                    <p>Compliance doesn’t stop at our doors. Middleware holds our third-party partners and vendors to the same high standards we apply internally. Every vendor undergoes a rigorous due diligence process, security assessment, and contractual review to ensure they meet our compliance obligations. Continuous monitoring and periodic reassessments help us maintain an ecosystem where customer data is protected end-to-end, even when involving third parties.</p>`,
                }
            ]
        },
        {
            name: "Platform Security",
            heading: "Platform Security",
            points: [
                {
                    title: "Platform Security",
                    description: `
                    <p>Middleware’s platform is designed with security and safety at its core, ensuring that every user interaction, transaction, and data exchange is protected. From authentication to content controls, we embed strong safeguards that give customers confidence in the reliability and integrity of our services.</p>`
                },
                {
                    title: "Safety and Security",
                    description: `
                    <p>The foundation of our platform is built on secure-by-design principles. We continuously test, monitor, and refine our systems to reduce vulnerabilities and protect against emerging threats. Safety measures include:</p>
                    <ul>
                        <li>Secure development lifecycle (SDLC) practices</li>
                        <li>Rigorous code reviews and penetration testing</li>
                        <li>Automated monitoring to detect and prevent malicious activity</li>
                        <li>Redundancy and failover mechanisms to ensure resilience and availability</li>
                    </ul>`,
                },
                {
                    title: "Security Contacts",
                    description: `
                    <p>We make it easy for customers and partners to contact our dedicated security team. Middleware maintains transparent and responsive security communication channels for:</p>
                    <ul>
                        <li>Reporting suspicious activity</li>
                        <li>Receiving updates on security incidents and mitigation steps</li>
                        <li>Coordinating with trusted third parties under responsible disclosure guidelines</li>
                    </ul>
                    <p>This ensures that issues are addressed quickly and responsibly, keeping the platform safe for all users.</p>`,
                },
                {
                    title: "Content Safety",
                    description: `
                    <p>To protect customers from misuse or abuse of the platform, Middleware implements robust content safety controls. This includes:</p>
                    <ul>
                        <li>Automated filters to prevent malicious uploads or harmful content</li>
                        <li>Continuous monitoring for unusual activity patterns</li>
                        <li>Strict enforcement of terms of service to safeguard the platform from abusive behavior</li>
                    </ul>
                    <p>By prioritizing content safety, we maintain trust and create a secure environment for all users.</p>`,
                },
                {
                    title: "Password Safety",
                    description: `
                    <p>Passwords remain one of the first lines of defense, and Middleware enforces strong controls to keep them secure:</p>
                    <ul>
                        <li>Support for strong, complex passwords with configurable policies</li>
                        <li>Secure hashing and salting of all stored credentials</li>
                        <li>Regular password rotation recommendations and detection of compromised credentials</li>
                    </ul>
                    <p>These measures ensure that accounts remain secure and resilient against unauthorized access.</p>`,
                }
            ]
        }
    ];

    const renderContent = () => {
        const tabContent = content.find(tab => tab.name === activeTab);
        if (!tabContent) return null;

        return (
            <div className="security-content">
                {/* <div className="security-header">
                    <h2>{tabContent.heading}</h2>
                </div> */}
                <div className="security-points">
                    {tabContent.points.map((point, index) => (
                        <div key={index} className="security-point">
                            <h2>{point.title}</h2>
                            {point.description && point.description.includes('<') ? (
                                <div 
                                    className="description-html"
                                    dangerouslySetInnerHTML={{ __html: point.description }}
                                />
                            ) : (
                                <p>{point.description}</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return <>
        <section className="content-hero">
            <div className="container">
                <div className="d-flex">
                    <div className="left-block">
                        <h1>Building customer trust by ensuring
                        <span className="text-gradient"> data security</span></h1>
                        <a href="https://middleware.io/contact-us/"><button className="primary ahref-btn">Contact us Now</button></a>
                    </div>
                    <div className="right-block">
                        <img src="https://middleware.io/backend/wp-content/uploads/2025/07/security-hero.webp" width="100" height="100" alt="security-middleware" loading="lazy" />
                    </div>
                </div>
            </div>
            {/* <TrustedCompany /> */}
        </section>

        <section className="content-inner">
            <div className="container">
                <div className="security-tabs">
                    <div className="tab-content">
                    <div className="top-tabs">
                                <ul>
                                    {tabs.map((tab) => (
                                        <li
                                            key={tab}
                                            className={activeTab === tab ? "active" : ""}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            <p>{tab}</p>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="right-content">
                                {renderContent()}
                            </div>
                    </div>
                </div>
            </div>
        </section>
        <CallToActionSmall/>
    </>
}

export default Security
